import * as React from 'react'
import { useSubscribe } from 'replicache-react'
import type { ReadonlyJSONValue, ReadTransaction, Replicache } from 'replicache'
import {
  QueryKey,
  useQuery,
  hashQueryKey,
  useQueryClient,
} from '@tanstack/react-query'
import { ExpectedSyncerError } from '@basehub/data/replicache/syncers'

export const useReplicacheQuery = <T extends ReadonlyJSONValue>({
  replicache,
  deps,
  fetcher,
  enabled = true,
  initialState,
}: {
  replicache: Replicache | undefined
  deps: React.DependencyList
  fetcher: (tx: ReadTransaction) => Promise<T>
  enabled?: boolean
  initialState?: T
}):
  | {
      isLoading: false
      data: T
    }
  | {
      isLoading: true
      data: undefined | T
    } => {
  const memoDeps = React.useMemo(
    () => [replicache, enabled, ...deps],
    [replicache, enabled, deps]
  )
  const [isLoading, setIsLoading] = React.useState(true)

  const subscriberResult = useSubscribe(
    replicache,
    async (tx) => {
      if (!enabled) {
        setIsLoading(true)
        return initialState
      }
      const result = await fetcher(tx)
      setIsLoading(false)
      return result
    },
    { default: initialState, dependencies: memoDeps }
  )

  return React.useMemo(() => {
    if (isLoading || subscriberResult === undefined) {
      return {
        isLoading: true,
        data: subscriberResult,
      }
    }

    return {
      isLoading: false,
      data: subscriberResult,
    }
  }, [isLoading, subscriberResult])
}

const subscribers = new Set<string>()

export const useReplicacheQueryV2 = <T extends ReadonlyJSONValue>({
  key,
  replicache,
  fetcher,
  enabled = true,
  initialData,
  onData,
  keepPreviousData,
}: {
  key: QueryKey
  replicache: Replicache | undefined
  fetcher: (tx: ReadTransaction) => Promise<T>
  enabled?: boolean
  initialData?: T
  onData?: (data: T) => void
  keepPreviousData?: boolean
}) => {
  const queryClient = useQueryClient()
  const isEnabled = !!replicache && enabled

  const fetcherRef = React.useRef(fetcher)
  // eslint-disable-next-line react-compiler/react-compiler
  fetcherRef.current = fetcher

  const keyRef = React.useRef(key)
  // eslint-disable-next-line react-compiler/react-compiler
  keyRef.current = key

  const onDataRef = React.useRef(onData)
  // eslint-disable-next-line react-compiler/react-compiler
  onDataRef.current = onData

  const query = useQuery<T>(
    key,
    async () => {
      if (!replicache) throw new Error('should not be enabled')
      return await replicache?.query(fetcher)
    },
    {
      enabled: isEnabled,
      initialData,
      staleTime: Infinity, // never stale, subscriber will update directly.
      keepPreviousData,
      retry: true,
    }
  )

  const hashedQueryKey = React.useMemo(() => hashQueryKey(key), [key])

  // notify existing hooks if there's a free spot
  const shouldSubscribe = isEnabled && !subscribers.has(hashedQueryKey)

  React.useEffect(() => {
    if (subscribers.has(hashedQueryKey)) return
    if (!replicache || !isEnabled) return

    subscribers.add(hashedQueryKey)

    const unsub = replicache.subscribe(fetcherRef.current, {
      onData: (data) => {
        queryClient.setQueryData<T>(keyRef.current, data)
        onDataRef.current?.(data)
      },
      onError: (error) => {
        if (error instanceof ExpectedSyncerError) {
          // just ignore it
        } else {
          console.error(error)
        }
      },
    })

    return () => {
      unsub()
      subscribers.delete(hashedQueryKey)
    }
  }, [queryClient, replicache, isEnabled, hashedQueryKey, shouldSubscribe])

  return query
}
